import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DashboardLayout } from '../common/page_component/index.js';
import Alert from '../common/Alert.js';
import { contactService } from '../../services/contactService.js';
import { Plus, Mail, Filter, CheckCircle, ArrowLeft, Pencil, Trash2, MoreVertical, HandCoins, XCircle, CreditCard, Eye, EyeOff, X } from "lucide-react";
import Button from '../common/Button.js';
import { useTranslation } from 'react-i18next';


const EditContact = () => {
  const { contactId } = useParams();
  const [contact, setContact] = useState(null);
  const [reply, setReply] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchContact();
  }, [contactId]);

  const fetchContact = async () => {
    try {
      const response = await contactService.getContact(contactId);
      setContact(response.data.data);
      setReply(response.data.data.reply || '');
    } catch (err) {
      Alert.error('Failed to load contact data');
      console.error(err);
      navigate('/contact');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!reply.trim()) {
      Alert.error('Please enter a reply message');
      return;
    }

    setSubmitting(true);

    try {
      await contactService.sendReply(contactId, { reply });
      Alert.success('Reply sent successfully!');
      setTimeout(() => navigate('/contact'), 1500);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to send reply');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async () => {
    if (!contact?.reply && reply.trim() !== '') {
      const confirmed = await Alert.confirm(
        'You have unsaved changes. Are you sure you want to leave?',
        'Unsaved Changes'
      );
      if (!confirmed) return;
    }
    navigate('/contact');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
            <p className="text-sm sm:text-base text-gray-600">Loading contact data...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const isAlreadyReplied = contact?.reply;

  return (
    <DashboardLayout>
                {/* Header */}
                <div className="mb-4 sm:mb-6">
                  <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
                      <Mail className="w-6 h-6 sm:w-7 sm:h-7 text-gray-900" /> {isAlreadyReplied ? 'View Reply' : 'Reply to Contact'}
                  </h1>
                </div>

                {/* Already Replied Alert */}
                {isAlreadyReplied && (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6">
                    <div className="flex items-start gap-2 sm:gap-3">
                      <span className="text-xl sm:text-2xl flex-shrink-0">✅</span>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-green-900 font-semibold text-base sm:text-lg">Already Replied</h3>
                        <p className="text-green-700 text-xs sm:text-sm mt-1">
                          This contact has already been replied to on {formatDate(contact.replied_at)}. 
                          You can view the details below.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Contact Details Section */}
                <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 md:p-8 space-y-6 sm:space-y-8 mb-4 sm:mb-6">
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Contact Information
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-1">Full Name</label>
                        <p className="text-sm sm:text-base text-gray-900 font-medium">{contact.full_name}</p>
                      </div>
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-1">Email</label>
                        <p className="text-sm sm:text-base text-gray-900 font-medium break-all">{contact.email}</p>
                      </div>
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-1">Phone</label>
                        <p className="text-sm sm:text-base text-gray-900 font-medium">{contact.phone}</p>
                      </div>
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-1">Location</label>
                        <p className="text-sm sm:text-base text-gray-900 font-medium">{contact.location || 'N/A'}</p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Message Details
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-1">Subject</label>
                        <p className="text-sm sm:text-base text-gray-900 font-medium">{contact.subject}</p>
                      </div>
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-2">Message</label>
                        <div className="bg-gray-50 rounded-xl p-3 sm:p-4 border border-gray-200">
                          <p className="text-sm sm:text-base text-gray-900 leading-relaxed whitespace-pre-wrap">
                            {contact.message}
                          </p>
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-1">Date Received</label>
                        <p className="text-sm sm:text-base text-gray-900 font-medium break-words">{formatDate(contact.created_at)}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Reply Section */}
                {isAlreadyReplied ? (
                  <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 md:p-8 space-y-6 sm:space-y-8">
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Reply Sent
                      </h3>
                      <div className="space-y-4 sm:space-y-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-2">Reply Message</label>
                          <div className="bg-green-50 rounded-xl p-3 sm:p-4 border border-green-200">
                            <p className="text-sm sm:text-base text-gray-900 leading-relaxed whitespace-pre-wrap">
                              {contact.reply}
                            </p>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-1">Replied By</label>
                            <p className="text-sm sm:text-base text-gray-900 font-medium">{contact.replied_by_name || 'N/A'}</p>
                          </div>
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-1">Replied At</label>
                            <p className="text-sm sm:text-base text-gray-900 font-medium break-words">{formatDate(contact.replied_at)}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4 sm:pt-6 border-t">
                      <Button
                        variant="secondary"
                        onClick={() => navigate('/contact')}
                      >
                        ← Back to Contacts
                      </Button>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm p-4 sm:p-6 md:p-8 space-y-6 sm:space-y-8">
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Send Reply
                      </h3>
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-900 mb-2">
                          Reply Message <span class="text-red-500 ml-1">*</span>
                        </label>
                        <textarea
                          value={reply}
                          onChange={(e) => setReply(e.target.value)}
                          placeholder="Enter your reply message here..."
                          required
                          rows={6}
                          className="w-full px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none"
                        />
                        <p className="text-xs sm:text-sm text-gray-600 mt-2">
                          {reply.length} characters
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4 sm:pt-6 border-t">
                      <Button
                        variant="primary"
                        type="submit"
                        loading={submitting}
                        disabled={submitting}
                      >
                        Send Reply
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={handleCancel}
                        disabled={submitting}
                      >
                        Cancel
                      </Button>
                    </div>
                  </form>
                )}
    </DashboardLayout>
  );
};

export default EditContact;